import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "normalTypeSelect",
    "guidedTourTypeSelect",
    "workspaceSelect",
    "responsibleForDay",
    "responsibleForDayWarning",
    "responsibleChangedToAbsenceWarning",
    "shiftTitle",
    "start",
    "end"
  ]

  connect() {
    // Init workspace select
    if (
      this.hasNormalTypeSelectTarget &&
      this.hasWorkspaceSelectTarget &&
      this.normalTypeSelectTarget.value === "regular"
    ) {
      this.showWorkSpaceSelect()
      this.enableResponsibleForDay()
    } else {
      this.disableResponsibleForDay()
    }

    if (
      this.hasResponsibleForDayWarningTarget &&
      this.responsibleForDayTarget.checked &&
      this.responsibleForDayTarget.getAttribute(
        "data-others-are-responsible"
      ) === "true"
    ) {
      this.responsibleDayWarningTarget.classList.remove(
        "d-none"
      )
      this.responsibleDayWarningTarget.classList.add(
        "d-block"
      )
    }

    // Init which type dropdown to show. Preserve the server-rendered type for
    // existing shifts (absences clear workspace_id, so do not infer from the
    // workspace select alone — that incorrectly forced "Dienst" / regular).
    this.initShiftTypeSelect()

    // Refresh Trix editors when tab becomes visible (they don't render properly in hidden tabs)
    const tabPane = this.element.closest(".tab-pane")
    if (tabPane) {
      const tabId = tabPane.id
      const tabButton = document.querySelector(`[data-bs-toggle="tab"][href="#${tabId}"]`)
      if (tabButton) {
        tabButton.addEventListener("shown.bs.tab", () => {
          this.refreshTrixEditors()
        })
      }
    }
  }

  // Guide-only form has neither dual type selects nor workspaceSelect target
  initShiftTypeSelect() {
    if (
      !this.hasWorkspaceSelectTarget ||
      !this.hasNormalTypeSelectTarget ||
      !this.hasGuidedTourTypeSelectTarget
    ) {
      return
    }

    const workspaceSelect =
      this.workspaceSelectTarget.querySelector("select")
    if (!workspaceSelect) {
      return
    }

    const selectedOption =
      workspaceSelect.selectedIndex >= 0
        ? workspaceSelect.options[workspaceSelect.selectedIndex]
        : null
    const isGuideWorkspace =
      selectedOption?.dataset?.forGuides === "true"
    const currentType = this.shiftTypeFromServer()
    const guideTypes = ["guide_tour", "guide_canceled", "guide_other"]
    const absenceTypes = [
      "vacation",
      "special_vacation",
      "sick_leave",
      "care_leave",
      "doctors_visit",
      "compensatory_time",
      "exemption",
      "other_absence"
    ]
    const persisted = this.isPersistedShift()

    // Absences / außerordentlicher Dienst: always keep normal dropdown + type
    if (
      absenceTypes.includes(currentType) ||
      currentType === "extraordinary"
    ) {
      this.showNormalShiftTypes()
      return
    }

    if (guideTypes.includes(currentType) || isGuideWorkspace) {
      this.showGuideShiftTypes()
      // Default only for new shifts on guide workspaces
      if (!persisted) {
        this.guidedTourTypeSelectTarget.value = "guide_other"
      }
    } else {
      this.showNormalShiftTypes()
      if (!persisted) {
        this.normalTypeSelectTarget.value = "regular"
      }
    }
  }

  shiftTypeFromServer() {
    if (
      this.hasGuidedTourTypeSelectTarget &&
      !this.guidedTourTypeSelectTarget.classList.contains("d-none")
    ) {
      return this.guidedTourTypeSelectTarget.value
    }
    if (this.hasNormalTypeSelectTarget) {
      return this.normalTypeSelectTarget.value
    }
    return ""
  }

  isPersistedShift() {
    return Boolean(
      this.element.querySelector(
        'input[name="_method"][value="patch"]'
      ) ||
        this.element.querySelector(
          'input[name="_method"][value="put"]'
        )
    )
  }

  refreshTrixEditors() {
    // Trix editors need to be re-initialized when their container becomes visible
    // Force a resize event to trigger Trix to recalculate its dimensions
    this.element.querySelectorAll("trix-editor").forEach((editor) => {
      const toolbar = editor.previousElementSibling
      if (toolbar && toolbar.tagName === "TRIX-TOOLBAR") {
        // Toggle visibility to force Trix to recalculate dimensions
        editor.style.display = "none"
        editor.offsetHeight // Force reflow
        editor.style.display = ""
      }
    })
  }

  toggleResponsibleChangedToAbsenceWarning(event) {
    if (
      event.target.value !== "regular" &&
      event.target.value !== "extraordinary" &&
      this.hasResponsibleChangedToAbsenceWarningTarget &&
      this.responsibleForDayTarget.checked
    ) {
      this.responsibleChangedToAbsenceWarningTarget.classList.remove(
        "d-none"
      )
      this.responsibleChangedToAbsenceWarningTarget.classList.add(
        "d-block"
      )
    }
  }

  showWorkSpaceSelect() {
    this.workspaceSelectTarget.classList.remove("d-none")
    this.workspaceSelectTarget.classList.add("d-block")
  }

  hideWorkSpaceSelect() {
    this.workspaceSelectTarget.classList.remove("d-block")
    this.workspaceSelectTarget.classList.add("d-none")
  }

  toggleWorkspaceSelect(event) {
    if (!this.hasWorkspaceSelectTarget) {
      return
    }

    const workspaceSelect = this.workspaceSelectTarget.querySelector("select")
    const currentWorkspace = workspaceSelect ? workspaceSelect.options[workspaceSelect.selectedIndex] : null
    const isGuideWorkspace = currentWorkspace && currentWorkspace.dataset.forGuides === "true"

    // Define absence types
    const absenceTypes = [
      "vacation",
      "special_vacation",
      "sick_leave",
      "care_leave",
      "doctors_visit",
      "compensatory_time",
      "exemption",
      "other_absence"
    ]

    // For absence types, hide workspace selector, reset value, and switch to normal shift types
    if (absenceTypes.includes(event.target.value)) {
      if (workspaceSelect) {
        workspaceSelect.value = ""
      }
      this.hideWorkSpaceSelect()
      // Switch to normal shift types for absence types
      this.showNormalShiftTypes()
      // Sync the value to normalTypeSelect if coming from guidedTourTypeSelect
      if (event.target === this.guidedTourTypeSelectTarget) {
        this.normalTypeSelectTarget.value = event.target.value
      }
      return
    }

    // For guide workspaces, show workspace selector for non-absence shift types
    if (isGuideWorkspace) {
      this.showWorkSpaceSelect()
      return
    }

    // For non-guide workspaces, handle shift type changes
    if (event.target.value === "regular") {
      this.showWorkSpaceSelect()
      // Always switch to first non-guide workspace when selecting regular shift type
      if (workspaceSelect && this.hasNonGuideWorkspace()) {
        const nonGuideOption = Array.from(workspaceSelect.options).find(
          (opt) => opt.dataset.forGuides !== "true"
        )
        if (nonGuideOption) {
          workspaceSelect.value = nonGuideOption.value
        }
      }
    } else if (absenceTypes.includes(event.target.value)) {
      // Reset workspace to blank for absence types and hide selector
      if (workspaceSelect) {
        workspaceSelect.value = ""
      }
      this.hideWorkSpaceSelect()
    } else {
      this.hideWorkSpaceSelect()
    }

    // If selecting an absence type from guidedTourTypeSelect, switch back to normalTypeSelect
    // This prevents users from getting stuck without access to regular shift types
    if (
      event.target === this.guidedTourTypeSelectTarget &&
      absenceTypes.includes(event.target.value)
    ) {
      // Only switch to normal shift types if a non-guide workspace exists
      // Otherwise stay in guide mode with current workspace
      if (this.hasNonGuideWorkspace()) {
        this.showNormalShiftTypes()
        // Sync the value to normalTypeSelect
        this.normalTypeSelectTarget.value = event.target.value
        // Hide workspace since absence types don't need workspace
        this.hideWorkSpaceSelect()
        // Reset workspace to blank for absence types
        if (workspaceSelect) {
          workspaceSelect.value = ""
        }
      }
    }
  }

  hasNonGuideWorkspace() {
    const workspaceSelect = this.workspaceSelectTarget.querySelector("select")
    if (!workspaceSelect) return false
    return Array.from(workspaceSelect.options).some(
      (opt) => opt.dataset.forGuides !== "true"
    )
  }

  enableResponsibleForDay() {
    this.responsibleForDayTarget.removeAttribute("disabled")
    this.responsibleForDayTarget.parentNode.classList.remove(
      "text-muted"
    )
  }

  disableResponsibleForDay() {
    this.responsibleForDayTarget.setAttribute(
      "disabled",
      "disabled"
    )
    this.responsibleForDayTarget.parentNode.classList.add(
      "text-muted"
    )
  }

  toggleResponsibleForDay(event) {
    if (!this.hasResponsibleForDayTarget) {
      return
    }

    if (event.target.value === "regular") {
      this.enableResponsibleForDay()
    } else {
      this.disableResponsibleForDay()
    }
  }

  changeResponsibleForDay(event) {
    if (
      event.target.checked &&
      event.target.getAttribute(
        "data-others-are-responsible"
      ) === "true"
    ) {
      this.responsibleForDayWarningTarget.classList.remove(
        "d-none"
      )
      this.responsibleForDayWarningTarget.classList.add(
        "d-block"
      )
    }
  }

  toggleShiftTitle(event) {
    if (!this.hasShiftTitleTarget) {
      return
    }

    this.shiftTitleTarget.classList.toggle(
      "d-none",
      event.target.value !== "extraordinary"
    )
  }

  timeStringToMinutes(timeString) {
    // Split the time string into hours and minutes
    const [hours, minutes] = timeString
      .split(":")
      .map(Number)
    // Convert the hours and minutes into total minutes
    return hours * 60 + minutes
  }

  minutesToTimeString(totalMinutes) {
    // Calculate hours and remaining minutes
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    // Format with leading zeros for minutes if needed
    const formattedMinutes = minutes.toString().padStart(2, '0');

    return `${hours}:${formattedMinutes}`;
  }

  addTimeStrings(fromTime, addTime) {
    // Convert time strings to minutes
    const fromMinutes = this.timeStringToMinutes(fromTime)

    const toMinutes = fromMinutes + addTime
    // Convert the difference back to time string
    return this.minutesToTimeString(toMinutes)
  }

  changeShiftTimes(event) {
    const dataset =
      event.target.options[event.target.selectedIndex]
        .dataset
    const fixedLengthTitleElement = Array.from(
      document.querySelectorAll("#fixed-length-subtitle")
    ).pop()
    const fixedLength = fixedLengthTitleElement ? parseInt(fixedLengthTitleElement.dataset?.fixedLength) : false

    this.startTarget._flatpickr.setDate(
      dataset.startAt,
      true
    )
    if (fixedLength) {
      this.endTarget._flatpickr.setDate(this.addTimeStrings(
        dataset.startAt, fixedLength
      ), true)
    } else {
      this.endTarget._flatpickr.setDate(dataset.endAt, true)
    }
  }

  changeShiftTypes(event) {
    const selectedOption = event.target.options[event.target.selectedIndex];
    const isGuideWorkspace = selectedOption.dataset.forGuides === 'true';

    if (isGuideWorkspace) {
      this.showGuideShiftTypes();
      // Set default shift type to guide_other for guide workspaces
      this.guidedTourTypeSelectTarget.value = "guide_other";
    } else {
      this.showNormalShiftTypes();
      // Set default shift type to regular for non-guide workspaces
      this.normalTypeSelectTarget.value = "regular";
      // Show workspace selector for regular shifts
      this.showWorkSpaceSelect();
    }
  }

  showNormalShiftTypes() {
    this.normalTypeSelectTarget.classList.remove("d-none")
    this.normalTypeSelectTarget.setAttribute(
      "name",
      "shift[type]"
    )
    this.guidedTourTypeSelectTarget.classList.add("d-none")
    this.guidedTourTypeSelectTarget.removeAttribute("name")
  }

  showGuideShiftTypes() {
    this.normalTypeSelectTarget.classList.add("d-none")
    this.normalTypeSelectTarget.removeAttribute("name")
    this.guidedTourTypeSelectTarget.classList.remove(
      "d-none"
    )
    this.guidedTourTypeSelectTarget.setAttribute(
      "name",
      "shift[type]"
    )
  }
}
